import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  ArrowLeft, Shield, Lock, Eye, Database, Trash2, Mail, Smartphone,
  Users, Sparkles, Activity, BarChart3, MessageSquare, CreditCard,
  Link as LinkIcon, Bell, Globe, FileCheck, AlertOctagon, RefreshCw,
  UserCheck, Baby,
} from 'lucide-react';

const Section = ({ icon: Icon, title, children, accent = 'stone' }) => {
  const accentMap = {
    stone:   'bg-stone-100 text-stone-700',
    rose:    'bg-rose-100 text-rose-700',
    sky:     'bg-sky-100 text-sky-700',
    amber:   'bg-amber-100 text-amber-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    violet:  'bg-violet-100 text-violet-700',
    red:     'bg-red-100 text-red-700',
    indigo:  'bg-indigo-100 text-indigo-700',
    blue:    'bg-blue-100 text-blue-700',
  };
  return (
    <Card className="p-6 md:p-8 bg-white border-stone-200 shadow-sm">
      <div className="flex items-start gap-4 mb-4">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${accentMap[accent]}`}>
          <Icon className="w-6 h-6" strokeWidth={1.6} />
        </div>
        <h2 className="text-xl md:text-2xl font-nunito font-bold text-[#2D2D2D] mt-2">
          {title}
        </h2>
      </div>
      <div className="text-[#57534E] leading-relaxed space-y-3 text-[15px]">
        {children}
      </div>
    </Card>
  );
};

const Bullets = ({ items }) => (
  <ul className="list-disc list-outside pl-5 space-y-1 marker:text-stone-400">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();
  const lastUpdated = 'May 25, 2026';

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="privacy-policy-page">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          className="mb-8 text-primary hover:text-primary/80"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>

        {/* Hero */}
        <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl p-8 mb-8 border border-stone-200">
          <div className="flex items-center gap-4 mb-4">
            <Shield className="w-12 h-12 text-primary" strokeWidth={1.5} />
            <div>
              <h1 className="text-3xl md:text-4xl font-nunito font-bold text-[#2D2D2D]">
                Privacy Policy
              </h1>
              <p className="text-[#57534E]">Last Updated: {lastUpdated}</p>
            </div>
          </div>
          <p className="text-[#57534E] leading-relaxed">
            Calm Pathways (&ldquo;Calm Pathways,&rdquo; &ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;) respects your privacy
            and is committed to protecting the information you share with us.
          </p>
        </div>

        <div className="space-y-6">
          <Section icon={Eye} title="Introduction" accent="stone">
            <p>
              Calm Pathways (&ldquo;Calm Pathways,&rdquo; &ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;) respects your
              privacy and is committed to protecting the information you share with us.
            </p>
            <p>
              This Privacy Policy explains how we collect, use, process, disclose, and safeguard information
              when you access or use the Calm Pathways website, mobile application, services, features,
              content, and related technologies (collectively, the &ldquo;Service&rdquo;).
            </p>
            <p>By using the Service, you consent to the practices described in this Privacy Policy.</p>
          </Section>

          <Section icon={FileCheck} title="Nature of the Service" accent="sky">
            <p>
              Calm Pathways is a consumer wellness, educational, and organizational platform intended for use by
              parents, guardians, and caregivers navigating complex neuroimmune-related experiences and wellness tracking.
            </p>
            <p>
              The Service is not a healthcare provider, medical practice, emergency service, or clinical treatment platform.
            </p>
            <p>
              Unless explicitly stated otherwise in a separate written agreement, Calm Pathways may not be subject
              to HIPAA in all circumstances.
            </p>
            <p>Children do not independently create accounts or directly operate the Service.</p>
          </Section>

          <Section icon={Database} title="Information We Collect" accent="indigo">
            <p>We may collect the following categories of information:</p>

            <div>
              <h3 className="font-semibold text-[#2D2D2D] mt-3 mb-2">Account Information</h3>
              <Bullets items={[
                'Name',
                'Email address',
                'Password (encrypted and hashed)',
                'Subscription and billing information',
                'Account preferences',
              ]} />
            </div>

            <div>
              <h3 className="font-semibold text-[#2D2D2D] mt-4 mb-2">Child-Related Information Entered by Parents or Caregivers</h3>
              <p>Parents, guardians, or caregivers may voluntarily enter information related to children, including:</p>
              <Bullets items={[
                'symptom tracking information,',
                'behaviors,',
                'emotional observations,',
                'food and nutrition logs,',
                'medication or supplement tracking,',
                'sleep information,',
                'environmental observations,',
                'appointment records,',
                'lab-related information,',
                'journal entries,',
                'uploaded documents or notes,',
                'AI prompts and related contextual information.',
              ]} />
              <p className="mt-2">All information entered into the Service is voluntarily provided by adult users.</p>
              <p>
                Parents and caregivers are responsible for ensuring they have appropriate authority to provide
                information entered into the Service.
              </p>
            </div>
          </Section>

          <Section icon={Smartphone} title="Usage and Device Information" accent="violet">
            <p>We may automatically collect technical information, including:</p>
            <Bullets items={[
              'device type,',
              'operating system,',
              'browser type,',
              'app version,',
              'general geographic region,',
              'IP address,',
              'diagnostic information,',
              'crash reports,',
              'feature usage information,',
              'and session activity.',
            ]} />
            <p>This information helps us:</p>
            <Bullets items={[
              'maintain app functionality,',
              'improve user experience,',
              'diagnose technical issues,',
              'and support security and operational integrity.',
            ]} />
          </Section>

          <Section icon={BarChart3} title="Analytics and Performance Monitoring" accent="emerald">
            <p>
              We may use analytics, crash reporting, and diagnostic tools to better understand:
            </p>
            <Bullets items={[
              'app performance,',
              'feature usage,',
              'reliability,',
              'and user interactions.',
            ]} />
            <p>
              These tools may collect limited technical information necessary to support operation and improvement of the Service.
            </p>
            <p>
              We make reasonable efforts to minimize unnecessary collection of personal information through analytics services.
            </p>
          </Section>

          <Section icon={Sparkles} title="AI Features and Processing" accent="violet">
            <p>
              Certain features within Calm Pathways may utilize artificial intelligence (&ldquo;AI&rdquo;) technologies.
            </p>
            <p>When AI-related features are used:</p>
            <Bullets items={[
              'prompts, questions, and related contextual information may be transmitted to third-party AI service providers for processing;',
              'AI-generated outputs are automated and may not always be accurate, complete, or appropriate;',
              'information entered into AI features may include child-related wellness or tracking information voluntarily submitted by parents or caregivers.',
            ]} />
            <p>
              We may utilize configurations designed to reduce retention or model training where available, but we
              cannot guarantee third-party processing practices beyond our reasonable control.
            </p>
            <p>
              Users should avoid submitting highly sensitive identifying information through AI features unless
              necessary for their intended use of the Service.
            </p>
          </Section>

          <Section icon={Activity} title="How We Use Information" accent="sky">
            <p>We may use collected information to:</p>
            <Bullets items={[
              'provide and maintain the Service;',
              'process subscriptions and payments;',
              'generate organizational summaries, trends, and correlations;',
              'improve app functionality and user experience;',
              'provide customer support;',
              'communicate account or service-related information;',
              'send optional educational or marketing communications where permitted;',
              'detect fraud, abuse, or security concerns;',
              'comply with legal obligations;',
              'and improve system performance and safety.',
            ]} />
            <p className="font-semibold text-[#2D2D2D]">
              We do not sell personal or child-related information to advertisers or data brokers.
            </p>
          </Section>

          <Section icon={Lock} title="De-Identified and Aggregated Information" accent="stone">
            <p>We may create and use de-identified, anonymized, or aggregated information for:</p>
            <Bullets items={[
              'analytics,',
              'research,',
              'service improvement,',
              'product development,',
              'system monitoring,',
              'statistical analysis,',
              'and operational optimization.',
            ]} />
            <p>Such information is designed not to reasonably identify individual users or children.</p>
          </Section>

          <Section icon={Users} title="Community Features" accent="emerald">
            <p>If the Service includes community forums, discussions, or peer-support features:</p>
            <Bullets items={[
              'information voluntarily shared by users may be visible to other users;',
              'users are responsible for the content they choose to share publicly;',
              'we encourage users not to post highly sensitive or identifying child-related information publicly.',
            ]} />
            <p>Community content is user-generated and may not be reviewed for medical accuracy.</p>
            <p>
              We are not responsible for how other users may use, interpret, or share publicly disclosed information.
            </p>
          </Section>

          <Section icon={CreditCard} title="Subscription and Payment Processing" accent="amber">
            <p>Subscription payments may be processed through third-party payment processors.</p>
            <p>We do not store full payment card information on our systems.</p>
            <p>
              Payment processors may collect and process billing information according to their own privacy
              policies and security practices.
            </p>
          </Section>

          <Section icon={LinkIcon} title="Third-Party Services" accent="indigo">
            <p>We may use trusted third-party service providers, including:</p>
            <Bullets items={[
              'cloud hosting providers,',
              'payment processors,',
              'AI service providers,',
              'analytics providers,',
              'customer support platforms,',
              'weather or environmental data providers,',
              'app store platforms,',
              'and infrastructure providers.',
            ]} />
            <p>These providers may process information on our behalf solely to support operation of the Service.</p>
            <p>
              We make reasonable efforts to work with reputable vendors that maintain commercially reasonable
              security and privacy practices.
            </p>
            <p>
              However, we cannot guarantee the practices, security, availability, or performance of third-party services.
            </p>
          </Section>

          <Section icon={Smartphone} title="Apple iOS Users" accent="blue">
            <p>
              At this time, Calm Pathways does not use collected iOS user data for cross-app advertising tracking purposes.
            </p>
            <p>We currently utilize essential technologies for:</p>
            <Bullets items={[
              'authentication,',
              'session maintenance,',
              'subscription verification,',
              'app functionality,',
              'and user preferences.',
            ]} />
            <p>
              Practices may evolve as features develop, and users will be notified of material changes through
              updates to this Privacy Policy.
            </p>
          </Section>

          <Section icon={Shield} title="Data Security" accent="emerald">
            <p>
              We implement commercially reasonable administrative, technical, and organizational safeguards
              designed to help protect information, including:
            </p>
            <Bullets items={[
              'encryption in transit using TLS/SSL,',
              'secure authentication practices,',
              'password hashing,',
              'access controls,',
              'security monitoring,',
              'software updates,',
              'and secure cloud infrastructure practices.',
            ]} />
            <p>
              However, no method of internet transmission, electronic storage, or security system can be guaranteed
              completely secure.
            </p>
            <p>Accordingly, we cannot guarantee absolute security of information.</p>
          </Section>

          <Section icon={Database} title="Data Retention" accent="stone">
            <p>We retain information for as long as reasonably necessary to:</p>
            <Bullets items={[
              'provide the Service,',
              'maintain operational functionality,',
              'comply with legal obligations,',
              'resolve disputes,',
              'enforce agreements,',
              'prevent fraud or abuse,',
              'and maintain system integrity.',
            ]} />
            <p>
              Certain information may remain in backups, logs, archives, or security systems for a commercially
              reasonable period following deletion requests.
            </p>
          </Section>

          <Section icon={Trash2} title="Account Deletion and Data Requests" accent="rose">
            <p>Users may:</p>
            <Bullets items={[
              'access information associated with their account;',
              'export certain data;',
              'request correction of inaccurate information;',
              'or request account deletion.',
            ]} />
            <p>
              Upon verified deletion requests, we will delete or de-identify personal information within a
              commercially reasonable timeframe, except where retention is necessary for:
            </p>
            <Bullets items={[
              'legal obligations,',
              'fraud prevention,',
              'security purposes,',
              'operational backups,',
              'or enforcement of agreements.',
            ]} />
            <p>To request account deletion or privacy-related assistance, contact:</p>
            <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 mt-2">
              <a
                href="mailto:contact@meandmypandas.com"
                className="text-primary hover:underline font-medium"
              >
                contact@meandmypandas.com
              </a>
            </div>
          </Section>

          <Section icon={Baby} title="Children’s Privacy" accent="sky">
            <p>Calm Pathways is intended for use by parents, guardians, and caregivers only.</p>
            <p>Children under 18 should not independently create accounts or directly use the Service.</p>
            <p>
              Parents and caregivers may voluntarily enter child-related wellness or tracking information into the Service.
            </p>
            <p>
              We do not knowingly collect personal information directly from children through independent child accounts.
            </p>
            <p>
              If we become aware that a child has independently submitted information in violation of this Policy,
              we may remove such information and terminate associated accounts.
            </p>
          </Section>

          <Section icon={Globe} title="International Users" accent="indigo">
            <p>The Service is operated primarily within the United States.</p>
            <p>
              By using the Service, you understand and agree that information may be transferred to, processed,
              and stored in the United States or other jurisdictions where our service providers operate.
            </p>
            <p>Privacy laws in these jurisdictions may differ from those in your country of residence.</p>
          </Section>

          <Section icon={UserCheck} title="Your Privacy Rights" accent="emerald">
            <p>Depending on your jurisdiction, you may have rights to:</p>
            <Bullets items={[
              'access personal information;',
              'correct inaccurate information;',
              'request deletion;',
              'request data portability;',
              'withdraw consent where applicable;',
              'and opt out of certain communications.',
            ]} />
            <p>We may require verification of identity before processing certain requests.</p>
          </Section>

          <Section icon={Bell} title="Marketing Communications" accent="amber">
            <p>
              You may opt out of promotional or marketing communications at any time using unsubscribe links or
              by contacting us directly.
            </p>
            <p>We may still send non-promotional communications related to:</p>
            <Bullets items={[
              'account management,',
              'billing,',
              'security,',
              'legal notices,',
              'and service functionality.',
            ]} />
          </Section>

          <Section icon={AlertOctagon} title="Security Incidents and Breach Notifications" accent="red">
            <p>
              In the event of a security incident involving personal information, we may notify affected users
              electronically, through the Service, by email, or by other methods as required by applicable law.
            </p>
          </Section>

          <Section icon={RefreshCw} title="Changes to This Privacy Policy" accent="stone">
            <p>We may update this Privacy Policy periodically.</p>
            <p>Material changes may be communicated through:</p>
            <Bullets items={[
              'app notifications,',
              'email communications,',
              'website notices,',
              'or updated posting dates.',
            ]} />
            <p>
              Continued use of the Service after changes become effective constitutes acceptance of the revised
              Privacy Policy.
            </p>
          </Section>

          <Section icon={Mail} title="Contact Information" accent="stone">
            <p>If you have questions regarding this Privacy Policy or our privacy practices, please contact:</p>
            <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 mt-2">
              <p className="font-semibold text-[#2D2D2D]">Me and My PANDAS LLC</p>
              <p>
                Email:{' '}
                <a
                  href="mailto:contact@meandmypandas.com"
                  className="text-primary hover:underline font-medium"
                >
                  contact@meandmypandas.com
                </a>
              </p>
            </div>
          </Section>
        </div>

        <div className="mt-10 p-6 bg-stone-50 border border-stone-200 rounded-xl text-center">
          <Shield className="w-8 h-8 text-stone-500 mx-auto mb-2" />
          <p className="text-sm text-[#57534E]">
            Last Updated: {lastUpdated} · By using Calm Pathways, you acknowledge that you have read,
            understood, and agree to this Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
